import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarDaysIcon, MapPinIcon, MessageSquareIcon, ReceiptTextIcon, SearchXIcon, ShieldAlertIcon, UsersIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card, EmptyState, KeyValue, SectionHeading } from '../../components/ui/Panels';
import { Badge } from '../../components/ui/Badge';
import { Button, ButtonLink } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { Field, Input, Textarea } from '../../components/ui/Field';
import { BookingTimeline } from '../../components/bookings/BookingTimeline';
import { statusLabel, statusTone } from '../../components/bookings/BookingCard';
import { QuoteCard } from '../../components/chat/QuoteCard';
import { useViewerRole } from '../../hooks/useViewerRole';
import { useAppData } from '../../contexts/AppDataContext';
import { useLocale } from '../../contexts/LocaleContext';
import { chefById } from '../../data/chefs';
import { counterpartById } from '../../data/counterparts';
import { allergySummary } from '../../data/customer';
import { currency, longDate } from '../../utils/format';
const refundByTier: Record<string, {
  copy: string;
  rate: number;
}> = {
  flexible: {
    copy: 'Flexible — full deposit refund up to 48 hrs before',
    rate: 1
  },
  moderate: {
    copy: 'Moderate — full refund up to 7 days before, then 50%',
    rate: 0.5
  },
  strict: {
    copy: 'Strict — the deposit is non-refundable once accepted',
    rate: 0
  }
};
export function BookingDetail() {
  const {
    bookingId
  } = useParams();
  const {
    bookingById,
    quoteVersions,
    threads,
    cancelBooking,
    addReceipt
  } = useAppData();
  const {
    locale,
    currencyCode
  } = useLocale();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [receiptLabel, setReceiptLabel] = useState('');
  const [receiptAmount, setReceiptAmount] = useState('');
  const role = useViewerRole();
  const booking = bookingById(bookingId);
  if (!booking) {
    return <AppShell title="Booking" data-id="element-2317" data-mp="2truu250qfwsu">
        <EmptyState icon={<SearchXIcon className="h-5 w-5" aria-hidden="true" data-id="element-2319" data-mp="2trb9shnm0wbn" />} title="Booking not found" body="This reference does not match anything in your account." action={<ButtonLink to={role === 'chef' ? '/chef/bookings' : '/app/bookings'} data-id="element-2320" data-mp="32ssqa14xj00v">Back to bookings</ButtonLink>} data-id="element-2318" data-mp="2tqt7p4e9nnvt" />
      </AppShell>;
  }
  const chef = chefById(booking.chefId);
  const other = role === 'chef' ? counterpartById(booking.customerId) : chef;
  const versions = quoteVersions(booking.id);
  const current = versions.find(q => q.status === 'pending') ?? versions[versions.length - 1];
  const thread = threads.find(t => t.bookingId === booking.id);
  const tier = refundByTier[chef?.cancellationTier ?? 'moderate'];
  const refund = current ? Math.round(current.depositDue * tier.rate) : 0;
  const canCancel = booking.status === 'confirmed' || booking.status === 'quoted' || booking.status === 'requested';
  const receiptTotal = booking.groceryReceipts.reduce((sum, r) => sum + r.amount, 0);
  return <AppShell title={booking.ref} subtitle={`${booking.occasion} · ${longDate(booking.date, locale)}`} data-id="element-2321" data-mp="bnmpo79h3nbi">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-2322" data-mp="3txyek4k61iip">
        <div className="space-y-6" data-id="element-2323" data-mp="1w8iref6zl8c">
          <Card className="p-5" data-id="element-2324" data-mp="pljkoz8a3lo2">
            <div className="flex flex-wrap items-center gap-3" data-id="element-2325" data-mp="pmmd5bl91w63">
              <Avatar name={other?.name ?? 'Guest'} src={(other as {
              photo?: string;
            })?.photo} size="md" data-id="element-2326" data-mp="uknyk365e7kq" />
              <div className="min-w-0 flex-1" data-id="element-2327" data-mp="gl4xtvfo1yig">
                <p className="font-display text-lg font-semibold text-ink" data-id="element-2328" data-mp="gkd1gie8vwr4">{other?.name}</p>
                <p className="text-[13px] text-ink-2" data-id="element-2329" data-mp="lin39buh5vlp">{booking.menuName ?? 'Menu still being agreed'}</p>
              </div>
              <Badge tone={statusTone[booking.status]} data-id="element-2330" data-mp="31d6v0mq1hwoq">{statusLabel[booking.status]}</Badge>
            </div>

            <dl className="mt-4" data-id="element-2331" data-mp="367ua5uz5leqn">
              <KeyValue label="When" value={<span className="flex items-center gap-1.5" data-id="element-2333" data-mp="3gtdjx093jwfh">
                    <CalendarDaysIcon className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" data-id="element-2334" data-mp="3grsol8eyk9gk" />
                    {longDate(booking.date, locale)} at {booking.time}
                  </span>} data-id="element-2332" data-mp="3lk3o4a5sdt8l" />
              <KeyValue label="Guests" value={<span className="flex items-center gap-1.5" data-id="element-2336" data-mp="8b7mmbkqugmy">
                    <UsersIcon className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" data-id="element-2337" data-mp="3fa727hb1eb8" />
                    {booking.guests}
                  </span>} data-id="element-2335" data-mp="37qm7ax4hium9" />
              <KeyValue label="Service" value={booking.fulfillment === 'in_home' ? 'Chef cooks on site' : 'Cooked and delivered'} data-id="element-2338" data-mp="3qjga6sou2wa4" />
              <KeyValue label="Where" value={<span className="flex items-center gap-1.5 text-right" data-id="element-2340" data-mp="2f2qalsex12ph">
                    <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" data-id="element-2341" data-mp="ulz99uuq5vro" />
                    {booking.addressLine}
                  </span>} data-id="element-2339" data-mp="2k3mggbituyxj" />
              <KeyValue label="Cancellation" value={<span className="text-[13px]" data-id="element-2343" data-mp="13llzdki07f5f">{tier.copy}</span>} data-id="element-2342" data-mp="13mnogsvuu265" />
            </dl>

            {thread && <div className="mt-4 flex flex-wrap gap-2" data-id="element-2344" data-mp="18eov9za970os">
                <ButtonLink to={`${role === 'chef' ? '/chef' : '/app'}/inbox/${thread.id}`} variant="outline" size="sm" data-id="element-2345" data-mp="1dcqeck7oj009">
                  <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-2346" data-mp="1ia8r6lothzdj" />
                  Open the conversation
                </ButtonLink>
                {role === 'customer' && chef && <ButtonLink to={`/chefs/${chef.slug}`} variant="ghost" size="sm" data-id="element-2347" data-mp="1rayh58recvjc">
                    View chef profile
                  </ButtonLink>}
              </div>}
          </Card>

          {current && <section aria-labelledby="quote-heading" data-id="element-2348" data-mp="3t5fnwojcxn2w">
              <SectionHeading level={2} title="The quote" hint={versions.length > 1 ? `${versions.length} versions — the newest is shown.` : undefined} data-id="element-2349" data-mp="109xit4uznzio" />
              <div className="mt-4" data-id="element-2350" data-mp="19b45jvo8e6kd">
                <QuoteCard quote={current} role={role} data-id="element-2351" data-mp="157z3fu82c6w9" />
              </div>
              {versions.length > 1 && <ul className="mt-3 space-y-1.5" data-id="element-2352" data-mp="1j775cuvm1t45">
                  {versions.filter(q => q.id !== current.id).map(q => <li key={q.id} className="flex items-baseline justify-between gap-3 rounded-lg bg-oat px-3 py-2 text-[13px]" data-id="element-2353" data-mp="1e6t6a2bzlti9">
                        <span className="text-ink-2" data-id="element-2354" data-mp="1nxxd2erie820">
                          Version {q.version} · {q.status}
                        </span>
                        <span className="tnum text-ink" data-id="element-2355" data-mp="21yhup7lotxtx">{currency(q.total, locale, currencyCode)}</span>
                      </li>)}
                </ul>}
            </section>}

          {(booking.groceryReceipts.length > 0 || role === 'chef') && <Card className="p-5" data-id="element-2356" data-mp="2lji39hyeuhhh">
              <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-ink" data-id="element-2357" data-mp="27k8ydefnn9st">
                <ReceiptTextIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-2358" data-mp="2blu3uepv0lsz" />
                Grocery receipts
              </h2>
              <p className="mt-1 text-sm text-ink-2" data-id="element-2359" data-mp="1xte9e2b0xw3o">
                On at-cost menus the guest pays what the chef paid. Every receipt is itemised here.
              </p>
              {booking.groceryReceipts.length ? <ul className="mt-4 divide-y divide-hairline" data-id="element-2360" data-mp="1nxuhdqzkzhk7">
                  {booking.groceryReceipts.map(receipt => <li key={receipt.id} className="flex items-baseline justify-between gap-3 py-2.5" data-id="element-2361" data-mp="1sszxuaa7s40w">
                      <span className="text-sm text-ink" data-id="element-2362" data-mp="1juocons49yfu">{receipt.label}</span>
                      <span className="tnum text-sm font-medium text-ink" data-id="element-2363" data-mp="1jwr0d15srfhe">
                        {currency(receipt.amount, locale, currencyCode)}
                      </span>
                    </li>)}
                  <li className="flex items-baseline justify-between gap-3 pt-2.5" data-id="element-2364" data-mp="1pehyjctqo7z6">
                    <span className="text-sm font-semibold text-ink" data-id="element-2365" data-mp="1pgkuselhixcc">Total</span>
                    <span className="tnum font-display text-base font-semibold text-ink" data-id="element-2366" data-mp="1lcmy0ajmtymg">
                      {currency(receiptTotal, locale, currencyCode)}
                    </span>
                  </li>
                </ul> : <p className="mt-4 text-[13px] text-ink-3" data-id="element-2367" data-mp="16nh1iuc6imi9">Nothing uploaded yet.</p>}

              {role === 'chef' && <form className="mt-4 flex flex-wrap items-end gap-3 border-t border-hairline pt-4" onSubmit={e => {
            e.preventDefault();
            const amount = Number(receiptAmount);
            if (!receiptLabel.trim() || !amount) return;
            addReceipt(booking.id, receiptLabel.trim(), amount);
            setReceiptLabel('');
            setReceiptAmount('');
          }} data-id="element-2368" data-mp="11q8k4vyuumi2">
                  <Field label="What you bought" id="r-label" className="min-w-[12rem] flex-1" data-id="element-2369" data-mp="3dd26y3jlcfw9">
                    <Input id="r-label" value={receiptLabel} onChange={e => setReceiptLabel(e.target.value)} placeholder="Khlong Toei market — crab, herbs" data-id="element-2370" data-mp="3iakje3wthd90" />
                  </Field>
                  <Field label="Amount" id="r-amount" className="w-32" data-id="element-2371" data-mp="2uk85mcew4mai">
                    <Input id="r-amount" type="number" min={0} value={receiptAmount} onChange={e => setReceiptAmount(e.target.value)} className="tnum" data-id="element-2372" data-mp="2zdjd8frj173h" />
                  </Field>
                  <Button type="submit" variant="primary" size="md" data-id="element-2373" data-mp="irbglsmyqnji">
                    Add receipt
                  </Button>
                </form>}
            </Card>}
        </div>

        <aside className="space-y-4" data-id="element-2374" data-mp="rq6ln1e9trl8">
          <Card className="p-5" data-id="element-2375" data-mp="wrw0ks4m6pel">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-2376" data-mp="wq1g1ex98zb1">Progress</h2>
            <div className="mt-4" data-id="element-2377" data-mp="3vaktd2pgbxcq">
              <BookingTimeline entries={booking.timeline} data-id="element-2378" data-mp="42opktnz47cg" />
            </div>
          </Card>

          <Card className="p-5" data-id="element-2379" data-mp="40c8eiq1i10g">
            <h2 className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-2380" data-mp="91i4mqiws6eq">
              <ShieldAlertIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-2381" data-mp="dt9yb9xd2s93" />
              Dietary record
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-2" data-id="element-2382" data-mp="3cge5p0jm71xz">{allergySummary()}</p>
            {role === 'customer' && <Link to="/app/account/dietary" className="mt-3 inline-block text-[13px] font-medium text-primary underline underline-offset-2" data-id="element-2383" data-mp="39xsjfhw7uzxv">
                Edit dietary profile
              </Link>}
          </Card>

          {booking.cancellation ? <Card className="border-danger/30 bg-danger-soft p-5" data-id="element-2384" data-mp="301z9m69vtkjs">
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-danger" data-id="element-2385" data-mp="2v00871xpcxph">Cancelled</h2>
              <p className="mt-2 text-[13px] text-danger" data-id="element-2386" data-mp="2r0rbppba2lq8">{booking.cancellation.reason}</p>
              <p className="tnum mt-2 text-sm font-semibold text-danger" data-id="element-2387" data-mp="2m7px7iezgo76">
                {currency(booking.cancellation.refund, locale, currencyCode)} refunded
              </p>
            </Card> : canCancel && <Card className="p-5" data-id="element-2388" data-mp="27b7j9pfhzq4d">
                <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-2389" data-mp="27bh1ury7qv01">Cancel this booking</h2>
                {cancelling ? <>
                    <p className="mt-2 text-[13px] text-ink-2" data-id="element-2390" data-mp="1sos3wujm0pvr">
                      {tier.copy}. You would get back{' '}
                      <span className="tnum font-semibold text-ink" data-id="element-2391" data-mp="1xoo4ejt3lz6k">{currency(refund, locale, currencyCode)}</span> of the
                      deposit.
                    </p>
                    <Textarea className="mt-3" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why are you cancelling?" aria-label="Reason for cancelling" data-id="element-2392" data-mp="1omevtzhe4fvt" />
                    <div className="mt-3 flex gap-2" data-id="element-2393" data-mp="22m6guk6bqwjb">
                      <Button variant="action" size="sm" disabled={reason.trim().length < 4} onClick={() => {
                cancelBooking(booking.id, reason.trim(), refund);
                setCancelling(false);
              }} data-id="element-2394" data-mp="27lj06uxzllh5">
                        Confirm cancellation
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setCancelling(false)} data-id="element-2395" data-mp="35f1ygydxczgi">
                        Keep it
                      </Button>
                    </div>
                  </> : <>
                    <p className="mt-2 text-[13px] text-ink-2" data-id="element-2396" data-mp="2qhhxe2k5fofm">{tier.copy}.</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setCancelling(true)} data-id="element-2397" data-mp="2mgfwlhal34bj">
                      Start a cancellation
                    </Button>
                  </>}
              </Card>}
        </aside>
      </div>
    </AppShell>;
}